import React, { useState } from 'react';
import { Search, X, Plus } from 'lucide-react';
import { useStore, Recipe } from '../../store/useStore';
import CreateMealModal from '../mealplanner/CreateMealModal';

interface AddMealModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate?: Date;
  mealType?: string;
  onAddMeal: (recipe: Recipe) => void;
}

const AddMealModal: React.FC<AddMealModalProps> = ({
  isOpen,
  onClose,
  selectedDate = new Date(), // Add default value
  mealType = 'breakfast', // Add default value
  onAddMeal
}) => {
  const recipes = useStore((state) => state.recipes);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const filteredRecipes = recipes.filter(recipe =>
    recipe.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddMeal = () => {
    if (selectedRecipe) {
      onAddMeal(selectedRecipe);
      onClose();
    }
  };

  const handleCreateMeal = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setShowCreateModal(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
          <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                Add Meal for{' '}
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mt-6 flex justify-between items-center gap-4">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 px-4 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 whitespace-nowrap"
              >
                <Plus size={18} className="mr-2" />
                Create Recipe
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredRecipes.map((recipe) => (
                <div
                  key={recipe.id}
                  onClick={() => setSelectedRecipe(recipe)}
                  className={`cursor-pointer rounded-lg border ${selectedRecipe?.id === recipe.id
                      ? 'border-secondary ring-1 ring-secondary'
                      : 'border-gray-200 dark:border-gray-700'
                    } overflow-hidden hover:border-secondary transition-colors`}
                >
                  <img
                    src={recipe.image}
                    alt={recipe.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-3">
                    <h4 className="font-medium text-gray-800 dark:text-white">{recipe.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {recipe.prepTime} min prep · {recipe.cookTime} min cook
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${recipe.difficulty === 'Easy'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : recipe.difficulty === 'Medium'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                        {recipe.difficulty}
                      </span>
                      {Array.isArray(recipe.category) && recipe.category.map((each) => <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        {each}
                      </span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredRecipes.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No recipes found matching your search.</p>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleAddMeal}
              disabled={!selectedRecipe}
              className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
            </button>
          </div>
        </div>
      </div>

      <CreateMealModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateMeal={handleCreateMeal}
      />
    </>
  );
};

export default AddMealModal;